import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const CATEGORIES = [
  { name: "Gen Z", img: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=800", to: "/genz", tagline: "for the algorithm-aware" },
  { name: "Gen Alpha", img: "https://images.unsplash.com/photo-1503944583220-79d8926ad5e2?w=800", to: "/genalpha", tagline: "handmade for tiny humans" },
];

export default function Home() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { limit: 8 } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="badge-swadeshi mb-6">🪔 {t("Est. 2026 · Made in India")}</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              {t("Every purchase,")} <br/>
              <span className="text-[#E25A24]">{t("a story")}</span> {t("from a maker.")}
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-lg leading-relaxed">
              {t("Discover handloom sarees from Varanasi, pottery from Jaipur, spices from Kerala — and hundreds of Swadeshi treasures, sold directly by the artisans who make them.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-shop-btn" className="btn-primary inline-flex items-center gap-2">
                {t("Explore the bazaar")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/vendor/apply" data-testid="hero-sell-btn" className="btn-outline">
                {t("Sell your craft")}
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <div><div className="font-serif text-2xl text-[#13294B]">500+</div><div className="label-caps mt-1">{t("Artisans")}</div></div>
              <div><div className="font-serif text-2xl text-[#13294B]">28</div><div className="label-caps mt-1">{t("States")}</div></div>
              <div><div className="font-serif text-2xl text-[#13294B]">100%</div><div className="label-caps mt-1">{t("Swadeshi")}</div></div>
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-6 grid-rows-4 gap-3 h-[520px]">
            <div className="col-span-4 row-span-3 rounded-2xl overflow-hidden bg-[#F2EFEB]">
              <img src="https://images.unsplash.com/photo-1773847099204-238d283b2845?w=800" alt="Artisan" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-[#F2EFEB]">
              <img src="https://images.unsplash.com/photo-1591272216626-b09e38519371?w=600" alt="Spices" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-[#E25A24] p-6 flex flex-col justify-end text-white">
              <div className="label-caps opacity-80">{t("Featured")}</div>
              <div className="font-serif text-xl leading-tight mt-1">{t("Voices behind the craft")}</div>
            </div>
            <div className="col-span-4 row-span-1 rounded-2xl overflow-hidden bg-[#F2EFEB]">
              <img src="https://images.pexels.com/photos/20242195/pexels-photo-20242195.jpeg?w=800" alt="Pottery" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-caps mb-2">{t("Browse")}</div>
            <h2 className="text-3xl md:text-4xl">Shop by generation</h2>
          </div>
          <Link to="/products" className="text-sm text-[#E25A24] font-semibold hover:underline">{t("See all →")}</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={c.to}
              data-testid={`category-${c.name.toLowerCase().replace(' ', '-')}`}
              className="group card-flat overflow-hidden aspect-[16/10] ring-2 ring-transparent hover:ring-[#E25A24] transition-all"
            >
              <div className="relative w-full h-full">
                <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D141C] via-[#0D141C]/50 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFB07C] text-[#0D141C] text-[11px] font-black uppercase tracking-widest">
                  New
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-serif text-4xl md:text-5xl leading-none !text-[#FFB07C]" style={{color: "#FFB07C"}}>{t(c.name)}</div>
                  <div className="text-white/85 text-sm uppercase tracking-widest mt-3">{c.tagline}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-caps mb-2">{t("Featured")}</div>
            <h2 className="text-3xl md:text-4xl">{t("Fresh from the workshops")}</h2>
          </div>
          <Link to="/products" className="text-sm text-[#E25A24] font-semibold hover:underline">{t("Shop all →")}</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* VENDORS */}
      {vendors.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="mb-8">
            <div className="label-caps mb-2">{t("Meet the makers")}</div>
            <h2 className="text-3xl md:text-4xl">{t("Voices behind the craft")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {vendors.slice(0, 3).map((v) => (
              <Link key={v.id} to={`/vendor/${v.id}`} data-testid={`vendor-card-${v.id}`} className="card-flat p-6">
                <div className="flex items-center gap-4">
                  {v.avatar_url && <img src={v.avatar_url} alt={v.business_name} className="w-14 h-14 rounded-full object-cover" />}
                  <div>
                    <div className="font-serif text-lg text-[#13294B]">{v.business_name}</div>
                    <div className="text-xs text-[#718096]">{v.location}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-[#4A5568] line-clamp-3 leading-relaxed">{v.story || v.description}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
